import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Calendar } from 'lucide-react';
import SEOHead, { SITE_URL } from '../components/SEOHead';
import SafeImage from '../components/SafeImage';
import { useCalendlyEmbed } from '../context/CalendlyEmbedContext';
import './Trainers.css';

function Trainers() {
  const { openCalendly } = useCalendlyEmbed();
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrainers();
  }, []);

  const fetchTrainers = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'trainers'));
      const trainersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTrainers(trainersData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching trainers:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="trainers-page">
        <SEOHead
          title="Personal Trainers Palmetto Bay, Pinecrest & Kendall | BodyWorks Training"
          description="Certified personal trainers near Palmetto Bay, Pinecrest & Kendall — meet the BodyWorks Training coaching team serving Miami-Dade."
          canonical={`${SITE_URL}/trainers`}
          keywords="personal trainer near me, personal trainer Palmetto Bay, personal trainer Pinecrest, personal trainer Kendall, BodyWorks Training trainers, Bodyworks Training LLC"
        />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading trainers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="trainers-page">
      <SEOHead
        title="Personal Trainers Palmetto Bay, Pinecrest & Kendall | BodyWorks Training"
        description="Find a personal trainer near you: BodyWorks Training coaches serve Palmetto Bay, Pinecrest, Kendall & South Miami. Book a session online."
        canonical={`${SITE_URL}/trainers`}
        keywords="personal trainer near me, book personal training Palmetto Bay, book personal training Pinecrest, book personal training Kendall, BodyWorks Training trainers, Bodyworks Training LLC"
      />
      <section className="trainers-hero">
        <div className="trainers-hero-content">
          <h1 className="trainers-title">Our Trainers</h1>
          <p className="trainers-subtitle">Meet the elite team ready to transform your fitness journey</p>
        </div>
      </section>

      <section className="trainers-grid-section">
        {trainers.length === 0 ? (
          <div className="no-trainers">
            <p>No trainers found. Add some trainers in the Edit page!</p>
          </div>
        ) : (
          <div className="trainers-grid">
            {trainers.map((trainer) => (
              <div key={trainer.id} className="trainer-card">
                <div className="trainer-image-container">
                  <SafeImage
                    src={trainer.imageUrl}
                    alt={trainer.name}
                    className="trainer-image"
                    fallback={
                      <div className="trainer-image-placeholder">
                        <span>{trainer.name?.charAt(0) || 'T'}</span>
                      </div>
                    }
                  />
                </div>
                
                <div className="trainer-info">
                  <h3 className="trainer-name">{trainer.name}</h3>
                  <p className="trainer-specialty">{trainer.specialty}</p>
                  
                  {trainer.bio && (
                    <p className="trainer-bio">{trainer.bio}</p>
                  )}
                  
                  {trainer.certifications && trainer.certifications.length > 0 && (
                    <div className="trainer-certifications">
                      {trainer.certifications.map((cert, index) => (
                        <span key={index} className="cert-badge-small">{cert}</span>
                      ))}
                    </div>
                  )}
                  
                  {trainer.experience && (
                    <div className="trainer-experience">
                      <span className="experience-label">Experience:</span>
                      <span className="experience-value">{trainer.experience} years</span>
                    </div>
                  )}
                  
                  {trainer.calendlyUrl && (
                    <button
                      type="button"
                      className="book-session-btn"
                      onClick={() => openCalendly(trainer.calendlyUrl)}
                    >
                      <Calendar size={20} />
                      <span>Book a Session</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default Trainers;
